import fetchApi from 'utils/fetch';
import * as types from 'types/experience';
import { getEmbedConfig } from 'features/EmbedConfig';
import {
  PublicGetListingsEndpoint,
  GetListingsV3PublicEndpoint,
} from '@kouto/types';
import buildSessionsUrl from 'api/session/buildSessionsUrl';
import fetchSessions from 'api/session/fetchSessions';
import {
  normalizeListingV1,
  normalizeListingV3,
  shouldUseV3Listings,
} from 'utils/listingsHelpers';
import * as helpers from './helpers';

export const resetCurrentExperience = () => ({
  type: types.RESET_EXPERIENCE,
});
/**
 * Fetch one experience
 */
const fetchExperienceRequest = ({ id }) => ({
  type: types.FETCH_REQUEST,
  payload: {
    id,
  },
});

const fetchExperienceSuccess = ({ experience }) => ({
  type: types.FETCH_SUCCESS,
  payload: {
    experience,
  },
});

export const fetchExperience = (slug, options) => async (dispatch) => {
  if (!options.brandId) {
    throw new Error('options.brandId not set or invalid');
  }
  // const defaultBrandId = helpers.getDefaultBrandId();
  dispatch(fetchExperienceRequest({ id: slug }));
  try {
    const { data } = await fetchApi
      .get(
        `v1/brands/${options.brandId}/experiences/${slug}?latest=${
          options.latest ?? false
        }${options.languageCode ? `&language=${options.languageCode}` : ''}`,
      )
      .json();

    if (options.isStale?.()) return undefined;
    return dispatch(fetchExperienceSuccess({ experience: data }));
  } catch (errorResponse) {
    if (options.isStale?.()) return undefined;
    return dispatch(helpers.errorAction(types.FETCH_FAILURE, errorResponse));
  }
};
/**
 * List/Search
 */
const searchExperiencesRequest = () => ({
  type: types.SEARCH_REQUEST,
});

const searchExperiencesSuccess = ({ experiences, append, pagination }) => ({
  type: types.SEARCH_SUCCESS,
  payload: {
    append,
    experiences,
    pagination,
  },
});

export const searchExperiences =
  (query, options, append = false) =>
  async (dispatch) => {
    if (!options.brandId) {
      throw new Error('options.brandId not set or invalid');
    }

    const embedConfig = getEmbedConfig();

    dispatch(searchExperiencesRequest());
    const queryWithCategory = helpers.filterEmpty({
      isUnlisted: 'false',
      ...query,
      categoryIds: embedConfig?.categoryId
        ? [embedConfig.categoryId]
        : query.categoryIds ?? [],
    });
    const useV3 = shouldUseV3Listings(queryWithCategory);
    try {
      if (useV3) {
        delete queryWithCategory.bypassFirstAvailableDate;
        const listingsV3Url = GetListingsV3PublicEndpoint.url({
          query: {
            ...queryWithCategory,
            isUnlisted: queryWithCategory.isUnlisted === 'true',
          },
        });

        const { items, meta } = await fetchApi.get(listingsV3Url).json();

        const normalizedItems = items.map(normalizeListingV3);

        return dispatch(
          searchExperiencesSuccess({
            experiences: normalizedItems,
            pagination: meta,
            append,
          }),
        );
      }
      const listingsUrl = PublicGetListingsEndpoint.url({
        query: queryWithCategory,
      });

      const { items, meta } = await fetchApi
        .get(listingsUrl.slice(1, listingsUrl.length))
        .json();

      const normalizedItems = items
        .filter((item) => item.pictures?.length > 0)
        .map(normalizeListingV1);

      return dispatch(
        searchExperiencesSuccess({
          experiences: normalizedItems,
          pagination: meta,
          append,
        }),
      );
    } catch (errorResponse) {
      return dispatch(helpers.errorAction(types.SEARCH_FAILURE, errorResponse));
    }
  };

/**
 * Featured Experiences List
 */
const featuredExperiencesRequest = () => ({
  type: types.FEATURED_REQUEST,
});

const featuredExperiencesSuccess = ({ experiences }) => ({
  type: types.FEATURED_SUCCESS,
  payload: {
    experiences,
  },
});

export const featuredExperiences = (options, query) => async (dispatch) => {
  if (!options.brandId) {
    throw new Error('options.brandId not set or invalid');
  }
  dispatch(featuredExperiencesRequest());
  const embedConfig = getEmbedConfig();
  try {
    const { items } = await fetchApi
      .get(`v1/brands/${options.brandId}/experiences`, {
        searchParams: {
          ...query,
          categoryId: embedConfig.categoryId
            ? [embedConfig.categoryId]
            : query.categoryIds ?? [],
        },
      })
      .json();
    return dispatch(
      featuredExperiencesSuccess({
        experiences: items,
      }),
    );
  } catch (errorResponse) {
    return dispatch(helpers.errorAction(types.FEATURED_FAILURE, errorResponse));
  }
};

const getAvailableDatesRequest = ({ from, to }) => ({
  type: types.FETCH_AVAILABILITY_REQUEST,
  payload: {
    from,
    to,
  },
});

const getAvailableDatesSuccess = ({
  availabilities,
  fetchedUntilDate,
  shouldAppend,
}) => ({
  type: types.FETCH_AVAILABILITY_SUCCESS,
  shouldAppend,
  payload: {
    availabilities,
    fetchedUntilDate,
  },
});

export const getAvailableDates =
  (experience, { from, to, isPrivate, latest }, shouldAppend) =>
  async (dispatch) => {
    if (!experience.id) {
      throw new Error('experienceId is required');
    }
    dispatch(getAvailableDatesRequest({ from, to }));
    try {
      const searchOptions = { searchParams: {} };
      const endpointValue = experience?.isBookable
        ? 'dates'
        : 'dates-with-sessions';
      if (isPrivate) {
        searchOptions.searchParams.private = isPrivate;
      }
      if (latest) {
        searchOptions.searchParams.latest = latest;
      }

      const { items } = await fetchApi
        .get(
          `v2/experiences/${experience.id}/${endpointValue}/${from}/${to}`,
          searchOptions,
        )
        .json();
      return dispatch(
        getAvailableDatesSuccess({
          availabilities: items,
          fetchedUntilDate: to,
          shouldAppend,
        }),
      );
    } catch (errorResponse) {
      return dispatch(
        helpers.errorAction(types.FETCH_AVAILABILITY_FAILURE, errorResponse),
      );
    }
  };

const setNextAvailableDate = (nextAvailableDate) => ({
  type: types.SET_NEXT_AVAILABLE_DATE,
  payload: {
    nextAvailableDate,
  },
});

/**
 * Probe for the next date with availability on or after `from` and store it
 * in state (`nextAvailableDate`), without fetching its sessions.
 *
 * Probing `first-available-date` (instead of fetching the next range blindly)
 * skips gaps in availability of any length (e.g. nothing in September/October
 * but sessions in November). The UI only offers "see other dates" while a
 * next date is confirmed, and fetches that exact date's window on click.
 * The endpoint 404s when nothing is scheduled after `from`; that (or any
 * probe failure) stores null, which hides the button. (WAY-14831)
 */
export const checkNextAvailableDate =
  (experienceId, { from, isPrivate, latest }) =>
  async (dispatch) => {
    if (!experienceId) {
      throw new Error('experienceId is required');
    }
    try {
      const searchOptions = { searchParams: { from } };
      if (isPrivate) {
        searchOptions.searchParams.private = isPrivate;
      }
      if (latest) {
        searchOptions.searchParams.latest = latest;
      }

      const {
        data: { scheduledDate },
      } = await fetchApi
        .get(
          `v2/experiences/${experienceId}/first-available-date`,
          searchOptions,
        )
        .json();

      return dispatch(setNextAvailableDate(scheduledDate ?? null));
    } catch {
      return dispatch(setNextAvailableDate(null));
    }
  };

export const resetFirstAvailableDate = () => ({
  type: types.RESET_FIRST_AVAILABLE_DATE,
});

const getFirstAvailableDateRequest = ({ from }) => ({
  type: types.FETCH_FIRST_AVAILABLE_DATE_REQUEST,
  payload: {
    from,
  },
});

const getFirstAvailableDateSuccess = ({ firstAvailableDate, sessions }) => ({
  type: types.FETCH_FIRST_AVAILABLE_DATE_SUCCESS,
  payload: {
    firstAvailableDate,
    sessions,
  },
});

export const getFirstAvailableDate =
  (experienceId, { from, isPrivate, latest }) =>
  async (dispatch) => {
    if (!experienceId) {
      throw new Error('experienceId is required');
    }
    dispatch(getFirstAvailableDateRequest({ from }));
    try {
      const searchOptions = {
        searchParams: {},
      };

      if (isPrivate) {
        searchOptions.searchParams.private = isPrivate;
      }

      if (from) {
        searchOptions.searchParams.from = from;
      }

      if (latest) {
        searchOptions.searchParams.latest = latest;
      }

      const {
        data: { scheduledDate, sessions },
      } = await fetchApi
        .get(
          `v2/experiences/${experienceId}/first-available-date`,
          searchOptions,
        )
        .json();
      return dispatch(
        getFirstAvailableDateSuccess({
          firstAvailableDate: scheduledDate,
          sessions,
        }),
      );
    } catch (errorResponse) {
      return dispatch(
        helpers.errorAction(
          types.FETCH_FIRST_AVAILABLE_DATE_FAILURE,
          errorResponse,
        ),
      );
    }
  };

export const getAvailableSessionsRequest = ({ date, startDate, endDate }) => ({
  type: types.FETCH_SESSIONS_REQUEST,
  payload: {
    date,
    startDate,
    endDate,
  },
});

export const getAvailableSessionsSuccess = ({
  date,
  sessions,
  cartExperiences,
  experienceId,
  startDate,
  endDate,
}) => ({
  type: types.FETCH_SESSIONS_SUCCESS,
  payload: {
    date,
    sessions,
    cartExperiences,
    experienceId,
    startDate,
    endDate,
  },
});

export const getDateRangeAvailableSessions =
  (brandId, experienceId, startDate, endDate, cartItems) =>
  async (dispatch) => {
    if (!experienceId || !startDate || !endDate) {
      throw new Error('experienceId and startDate and endDate are required');
    }
    const cartExperiences = Object.values(cartItems).filter(
      (c) => c.sessionDate === startDate,
    );
    const url = buildSessionsUrl(experienceId, {
      from: startDate,
      to: endDate,
    });

    dispatch(getAvailableSessionsRequest({ startDate, endDate }));

    try {
      const result = await fetchSessions(url, { brandId });
      return dispatch(
        getAvailableSessionsSuccess({
          startDate,
          endDate,
          sessions: result?.sessions,
          experienceId,
          cartExperiences,
        }),
      );
    } catch (errorResponse) {
      return dispatch(
        helpers.errorAction(types.FETCH_SESSIONS_FAILURE, errorResponse),
      );
    }
  };

export const getAvailableSessions =
  (experienceId, date, query) => async (dispatch) => {
    if (!experienceId) {
      throw new Error('experienceId is required');
    }
    const cartExperiences = Object.values(query.cartExperienceList).filter(
      (c) => c.sessionDate === date,
    );

    dispatch(getAvailableSessionsRequest({ date }));
    try {
      const searchOptions = {
        ...(query?.isPrivate && { private: query.isPrivate }),
        searchParams: {},
      };

      if (query?.latest) {
        searchOptions.searchParams.latest = query.latest;
      }

      const { items } = await fetchApi
        .get(`v2/experiences/${experienceId}/sessions/${date}`, searchOptions)
        .json();

      return dispatch(
        getAvailableSessionsSuccess({
          date,
          sessions: items,
          cartExperiences,
          experienceId,
        }),
      );
    } catch (errorResponse) {
      return dispatch(
        helpers.errorAction(types.FETCH_SESSIONS_FAILURE, errorResponse),
      );
    }
  };

const getExperienceSettingsRequest = ({ experienceId }) => {
  return {
    type: types.FETCH_EXPERIENCE_SETTINGS_REQUEST,
    payload: {
      experienceId,
    },
  };
};

const getExperienceSettingsSuccess = ({ data }) => {
  return {
    type: types.FETCH_EXPERIENCE_SETTINGS_SUCCESS,
    payload: {
      data,
    },
  };
};

export const getExperienceSettings =
  (brandId, experienceId) => async (dispatch) => {
    dispatch(getExperienceSettingsRequest({ experienceId }));
    try {
      const { data } = await fetchApi
        .get(`v1/brands/${brandId}/experiences/${experienceId}/settings`)
        .json();
      return dispatch(getExperienceSettingsSuccess({ data }));
    } catch (errorResponse) {
      return dispatch(
        helpers.errorAction(
          types.FETCH_EXPERIENCE_SETTINGS_FAILURE,
          errorResponse,
        ),
      );
    }
  };

const getSessionPriceTiersRequest = ({ startDateTimes }) => {
  return {
    type: types.FETCH_SESSION_PRICE_TIERS_REQUEST,
    payload: {
      startDateTimes,
    },
  };
};

const getSessionPriceTiersSuccess = ({ items }) => {
  return {
    type: types.FETCH_SESSION_PRICE_TIERS_SUCCESS,
    payload: {
      items,
    },
  };
};

export const getSessionPriceTiers =
  (experienceId, startDateTimes) => async (dispatch) => {
    dispatch(getSessionPriceTiersRequest({ startDateTimes }));
    const queryParams = startDateTimes
      .map((value, i) => `startDateTimes[${i}]=${value}`)
      .join('&');

    try {
      const { items } = await fetchApi
        .get(
          `v2/experiences/${experienceId}/sessions/price-tiers?${queryParams}`,
        )
        .json();

      return dispatch(getSessionPriceTiersSuccess({ items }));
    } catch (errorResponse) {
      return dispatch(
        helpers.errorAction(
          types.FETCH_SESSION_PRICE_TIERS_FAILURE,
          errorResponse,
        ),
      );
    }
  };
